/**
 * Google Identity Services token client.
 *
 * Replaces gapi.auth2, which Google retired in March 2023 and which cannot
 * work in Safari: it confirmed sessions through an accounts.google.com iframe
 * that ITP will not grant storage access to. The visible symptom was an
 * endless sign-in loop on iOS.
 *
 * The models are not equivalent, and the differences drive the design here:
 *
 * - GIS issues **access tokens only**. There is no persistent session, no
 *   currentUser, no isSignedIn. "Are we authorised" is now just "do we hold an
 *   unexpired token carrying the scopes we need".
 * - Tokens last about an hour and there is no refresh token in this flow.
 * - Renewal is a fresh request with `prompt: ''`. That succeeds silently where
 *   the browser still sends Google's cookies, and fails on Safari - so on iOS
 *   a token cannot be obtained without a user gesture, and re-authorising is a
 *   button the user presses, not something we can do behind their back.
 *
 * requestToken({ interactive: true }) opens a popup and therefore MUST be
 * called synchronously from a user gesture. Awaiting anything first loses the
 * activation and iOS blocks the window.
 *
 * On iOS the popup is not merely fragile, it cannot work at all - see
 * gisRedirect.ts. There an interactive request navigates the page to Google
 * instead, so the promise it returns never settles. Callers must not depend on
 * authorize() resolving to continue; anything that has to happen afterwards
 * goes in the RedirectIntent and is replayed once the token lands.
 */

import { updateGapiToken } from '../serviceWorker/serviceWorker';
import * as redirect from './gisRedirect';

export type { RedirectIntent } from './gisRedirect';
export { canRenewSilently, shouldUseRedirect } from './gisRedirect';

const gisScriptUrl = 'https://accounts.google.com/gsi/client';

/** Refresh this long before expiry, so in-flight requests keep working. */
const refreshGraceMs = 5 * 60 * 1000;

/** A silent request that has not answered by now is not going to. */
const silentRequestTimeoutMs = 10000;

interface GisTokenResponse {
  access_token?: string;
  expires_in?: number;
  scope?: string;
  token_type?: string;
  error?: string;
  error_description?: string;
}

interface GisErrorResponse {
  type?: string;
  message?: string;
}

interface GisTokenClient {
  requestAccessToken(overrides?: { prompt?: string; scope?: string }): void;
}

interface GisOAuth2 {
  initTokenClient(config: {
    client_id: string;
    scope: string;
    prompt?: string;
    callback: (response: GisTokenResponse) => void;
    error_callback?: (error: GisErrorResponse) => void;
  }): GisTokenClient;
  revoke(token: string, done?: () => void): void;
}

declare global {
  interface Window {
    google?: {
      accounts?: {
        oauth2?: GisOAuth2;
      };
    };
  }
}

export interface GoogleToken {
  accessToken: string;
  /** Epoch ms. */
  expiresAt: number;
  /** Space separated, exactly as Google returned it. */
  scope: string;
}

export class GoogleAuthError extends Error {
  constructor(message: string, public readonly type?: string) {
    super(message);
    this.name = 'GoogleAuthError';
  }
}

let scriptPromise: Promise<void> | undefined;
let tokenClient: GisTokenClient | undefined;
let tokenClientScope = '';
let currentToken: GoogleToken | undefined;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;

/** Resolver for the request currently in flight. GIS has one callback per client. */
let pending: {
  resolve: (token: GoogleToken) => void;
  reject: (err: Error) => void;
  timer?: ReturnType<typeof setTimeout>;
} | undefined;

const listeners = new Set<(token: GoogleToken | undefined) => void>();

/** Notified whenever the token changes, including on expiry and revocation. */
export function onTokenChange(listener: (token: GoogleToken | undefined) => void) {
  listeners.add(listener);
  return function unsubscribe() {
    listeners.delete(listener);
  };
}

function publish() {
  listeners.forEach(listener => listener(currentToken));
}

export function loadGis() {
  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = gisScriptUrl;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Let a later attempt retry rather than caching the failure forever.
      scriptPromise = undefined;
      reject(new GoogleAuthError('Unable to load Google Identity Services.'));
    };

    document.head.appendChild(script);
  });

  return scriptPromise;
}

function settle(err?: Error, token?: GoogleToken) {
  if (!pending) {
    return;
  }

  const { resolve, reject, timer } = pending;
  pending = undefined;

  if (timer) {
    clearTimeout(timer);
  }

  if (err) {
    reject(err);
  }
  else {
    resolve(token!);
  }
}

/**
 * The redirect flow reloads the page, which takes every module-level variable
 * here with it - including the token Google just issued. So it has to outlive
 * the load.
 *
 * This is the same exposure the Dropbox token already has in `dbxToken`, and
 * an access token is the whole of what a GIS-era sign-in produces anyway:
 * about an hour of life, no refresh token behind it, revocable from
 * https://myaccount.google.com/permissions.
 */
const persistedTokenKey = 'gisToken';

function persist(token: GoogleToken | undefined) {
  try {
    if (token) {
      localStorage.setItem(persistedTokenKey, JSON.stringify(token));
    }
    else {
      localStorage.removeItem(persistedTokenKey);
    }
  }
  catch {
    // Private browsing. Sign-in still works for this page's lifetime; it just
    // will not survive the redirect, and the user sees the button again.
  }
}

/**
 * Adopt a token stored before a reload. Call once at startup, before deciding
 * whether the user is signed in.
 */
export function restorePersistedToken(): GoogleToken | undefined {
  if (currentToken) {
    return currentToken;
  }

  let stored: GoogleToken | undefined;

  try {
    const raw = localStorage.getItem(persistedTokenKey);
    stored = raw ? JSON.parse(raw) as GoogleToken : undefined;
  }
  catch {
    stored = undefined;
  }

  if (!stored || !stored.accessToken || !(stored.expiresAt > Date.now())) {
    persist(undefined);
    return undefined;
  }

  return activate(stored);
}

/**
 * What the user was in the middle of when they were sent to Google. Held in
 * module state rather than storage because the redirect route hands back to
 * the app through the router, which keeps this JS context alive. A full reload
 * would lose it, and that is a fair trade - the worst case is one extra tap.
 */
let redirectIntent: redirect.RedirectIntent | undefined;

/** Read once, by whoever acts on it. */
export function consumeRedirectIntent(): redirect.RedirectIntent | undefined {
  const intent = redirectIntent;
  redirectIntent = undefined;
  return intent;
}

/** Take a token the redirect flow brought back and make it the current one. */
export function adoptRedirectToken(result: redirect.RedirectResult): GoogleToken {
  redirectIntent = result.intent;

  return activate({
    accessToken: result.accessToken,
    expiresAt: Date.now() + (result.expiresIn * 1000),
    scope: result.scope,
  });
}

/**
 * The scopes the user has actually consented to, remembered across reloads.
 *
 * GIS issues a token for exactly what was asked for and keeps no memory of
 * past consent, so a startup request for `drive.appdata` produces a token that
 * can read the settings folder and nothing else - however much the user
 * granted months ago. Without this the app silently loses Drive access on
 * every reload and asks for it again, which is not what "already granted"
 * should feel like.
 *
 * Consent itself lives with Google; this is only a hint about what to ask for.
 * If it is stale, the worst case is a request Google declines to fill.
 */
const persistedScopesKey = 'gisGrantedScopes';

export function rememberedScopes(): string {
  try {
    return localStorage.getItem(persistedScopesKey) || '';
  }
  catch {
    return '';
  }
}

function rememberScopes(scope: string) {
  if (!scope) {
    return;
  }

  try {
    localStorage.setItem(persistedScopesKey, scope);
  }
  catch { }
}

function activate(token: GoogleToken) {
  currentToken = token;
  rememberScopes(token.scope);

  // The service worker attaches this to <audio> Drive requests, which cannot
  // set headers themselves.
  updateGapiToken(currentToken.accessToken);

  persist(currentToken);
  scheduleRefresh();
  publish();

  return currentToken;
}

function storeToken(response: GisTokenResponse) {
  return activate({
    accessToken: response.access_token!,
    // expires_in is seconds; treat a missing value as the documented default.
    expiresAt: Date.now() + ((response.expires_in ?? 3600) * 1000),
    scope: response.scope ?? '',
  });
}

/**
 * Attempt a silent renewal shortly before expiry.
 *
 * Best effort by design. Where third-party cookies are blocked this always
 * fails, and that is not an error worth showing anyone - the token simply
 * lapses and the UI asks for a fresh sign-in when something needs it.
 */
function scheduleRefresh() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
  }

  if (!currentToken) {
    return;
  }

  const delay = Math.max(currentToken.expiresAt - Date.now() - refreshGraceMs, 1000);

  refreshTimer = setTimeout(function refreshToken() {
    requestToken({ scope: tokenClientScope, interactive: false })
      .catch(() => {
        // Silent renewal is not available in this browser. Drop the token so
        // the UI can reflect that authorisation is needed again.
        if (currentToken && currentToken.expiresAt <= Date.now()) {
          currentToken = undefined;
          persist(undefined);
          publish();
        }
      });
  }, delay);
}

function ensureTokenClient(clientId: string, scope: string) {
  const oauth2 = window.google?.accounts?.oauth2;

  if (!oauth2) {
    throw new GoogleAuthError('Google Identity Services is not available.');
  }

  // The client is bound to a scope set at construction, so rebuild it whenever
  // the caller asks for something different.
  if (tokenClient && tokenClientScope === scope) {
    return tokenClient;
  }

  tokenClientScope = scope;
  tokenClient = oauth2.initTokenClient({
    client_id: clientId,
    scope,
    callback: (response: GisTokenResponse) => {
      if (response.error || !response.access_token) {
        settle(new GoogleAuthError(
          response.error_description || response.error || 'Google sign-in failed.',
          response.error,
        ));
        return;
      }

      // storeToken runs before settle looks at `pending`, and that order is
      // load-bearing: a token that arrives after the caller gave up waiting
      // is still a token the user just consented to. Activate it regardless;
      // whether anyone is still listening for the promise is a separate
      // question.
      settle(undefined, storeToken(response));
    },
    error_callback: (error: GisErrorResponse) => {
      // A popup that never opened is a blocker or a platform that does not
      // allow one - recoverable, because the redirect route needs no popup.
      // A popup the user closed is a deliberate cancellation; respect it.
      if (error.type === 'popup_failed_to_open' && pendingAllowRedirect) {
        redirect.beginRedirect(configuredClientId, tokenClientScope, pendingIntent);
        return;
      }

      settle(new GoogleAuthError(
        error.message || 'Google sign-in was cancelled or blocked.',
        error.type,
      ));
    },
  });

  return tokenClient;
}

let configuredClientId = '';

export function configureGis(clientId: string) {
  configuredClientId = clientId;
}

interface RequestOptions {
  scope: string;
  /**
   * true opens a popup and must be called straight from a user gesture.
   * false attempts a silent renewal, which fails wherever Google's cookies
   * are unavailable - Safari in particular.
   */
  interactive: boolean;
  /**
   * Whether this call may take the whole page to Google.
   *
   * Opt-in, because a redirect is destructive in a way a popup is not: it
   * tears the app down, and queued library and playlist writes have a 5 minute
   * debounce behind them. That is fine when the user has just pressed a
   * sign-in button and is expecting to go somewhere. It is not fine from an
   * effect - the player asks to authorize when it meets an unplayable Drive
   * track, and yanking someone out of the app mid-listen to fix that would be
   * its own bug.
   *
   * Callers that leave this false get the popup, and on iOS get a rejection
   * they can report. Re-authorising there stays a button the user presses.
   */
  allowRedirect?: boolean;
  /**
   * What to do once the token arrives, for the redirect route only. The page
   * is gone by then, so anything the caller meant to do after authorize()
   * has to be described here rather than written after an await.
   */
  intent?: redirect.RedirectIntent;
}

/** Carried for the popup-blocked fallback, which happens outside requestToken. */
let pendingIntent: redirect.RedirectIntent | undefined;
let pendingAllowRedirect = false;

/**
 * Navigating away means this promise never settles. That is deliberate and
 * safe: nothing chained onto it can run, because the page is being replaced.
 */
function goToRedirect(scope: string, intent?: redirect.RedirectIntent) {
  redirect.beginRedirect(configuredClientId, scope, intent);
  return new Promise<GoogleToken>(() => { });
}

export function requestToken(
  { scope, interactive, allowRedirect, intent }: RequestOptions,
): Promise<GoogleToken> {
  if (pending) {
    return Promise.reject(new GoogleAuthError('A Google sign-in is already in progress.'));
  }

  pendingIntent = intent;
  pendingAllowRedirect = !!allowRedirect;

  const mayRedirect = interactive && !!allowRedirect;

  // Where a popup cannot hand a token back - an iOS home-screen PWA above all
  // - do not open one. Going straight to the redirect avoids burning the
  // user's gesture on a window that was never going to reach us.
  if (mayRedirect && redirect.shouldUseRedirect()) {
    return goToRedirect(scope, intent);
  }

  let client: GisTokenClient;

  try {
    client = ensureTokenClient(configuredClientId, scope);
  }
  catch (err) {
    // GIS could not load at all - blocked script, offline, content blocker.
    // The redirect route is plain navigation and needs no library, so an
    // interactive sign-in can still go through.
    if (mayRedirect) {
      return goToRedirect(scope, intent);
    }

    return Promise.reject(err);
  }

  return new Promise<GoogleToken>((resolve, reject) => {
    pending = {
      resolve,
      reject,
      // GIS calls neither callback when a silent request has nothing to do,
      // so without this the promise would never settle.
      timer: interactive
        ? undefined
        : setTimeout(
          () => settle(new GoogleAuthError('Silent Google token renewal timed out.')),
          silentRequestTimeoutMs,
        ),
    };

    try {
      // prompt 'none' asks for no UI at all, which is what a renewal wants:
      // where a login or consent would be needed Google answers with an
      // error instead, and the promise settles at once.
      //
      // This used to be '', on the belief that it meant the same thing. It
      // does not. '' means "prompt only the first time the app asks" - and
      // after a user revokes the app at myaccount.google.com, the first time
      // is now. So a startup renewal opened a real consent popup, uninvited,
      // and the timer below raced the user through reading it: past ten
      // seconds the attempt was written off, the token then landed anyway
      // (see the callback above), and the user stood on the setup screen,
      // signed in, looking at a button that told them to sign in.
      //
      // Omitting prompt lets GIS show the account picker or consent screen
      // as needed, which is what a deliberate sign-in wants.
      client.requestAccessToken(interactive ? {} : { prompt: 'none' });
    }
    catch (err) {
      settle(err instanceof Error ? err : new GoogleAuthError(String(err)));
    }
  });
}

/** The current token, or undefined when absent or expired. */
export function getToken(): GoogleToken | undefined {
  if (currentToken && currentToken.expiresAt > Date.now()) {
    return currentToken;
  }

  return undefined;
}

export function getAccessToken() {
  return getToken()?.accessToken;
}

export function hasScope(scope: string) {
  const token = getToken();
  return !!token && token.scope.split(' ').indexOf(scope) !== -1;
}

export function grantedScopes() {
  return getToken()?.scope ?? '';
}

export function revokeToken() {
  const token = currentToken?.accessToken;

  currentToken = undefined;
  persist(undefined);

  // Revoking withdraws consent outright, so the remembered set is now a lie.
  try {
    localStorage.removeItem(persistedScopesKey);
  }
  catch { }

  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = undefined;
  }

  publish();

  if (token) {
    window.google?.accounts?.oauth2?.revoke(token);
  }
}
